// Aplikační potvrzovací dialog podle prototypu (`potvrdit({ titul, text, tlacitko, nebezpecne,
// duvod })`, data-screen-label="Potvrzení") — nikdy window.confirm/prompt (docs/04).
// Použití: `const dialog = useDialog(); const duvod = await dialog.potvrdit({...}); if (duvod === null) return;`
// Vrací zadaný důvod (prázdný řetězec bez pole důvodu), `null` = Zrušit / Esc / klik mimo (klik mimo jen u dialogu
// bez polí a jen když stisk začal i skončil na pozadí — `podklad.ts`).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { usePodklad } from './podklad';
import './komponenty.css';

export interface MoznostiDialogu {
  titul: string;
  text?: string;
  /**
   * Obsah pod textem (souhrn náhledu ze serveru: počty, částky, varování) — čísla dodává volající,
   * dialog je jen ukáže. Bez něj je dialog stejný jako dřív.
   */
  obsah?: ReactNode;
  /** Text potvrzovacího tlačítka podle slovníku (Smazat, Odebrat, Ukončit…). Výchozí „Potvrdit“. */
  tlacitko?: string;
  /** Text odmítavého tlačítka, když „Zrušit“ nesedí (např. „Obnovit později“). Výchozí „Zrušit“. */
  zrusit?: string;
  /** Červené plné tlačítko (mazání, storno). Výchozí true jako v prototypu. */
  nebezpecne?: boolean;
  /** Popisek pole důvodu; bez něj se pole nezobrazí. */
  duvod?: string;
  /** Důvod je povinný (výchozí true, když je pole zobrazené). */
  duvodPovinny?: boolean;
  /** Předvyplněný důvod. */
  vychozi?: string;
  /**
   * Potvrzení opsáním (nevratné smazání důležitého záznamu): tlačítko se odešle, až když se do pole opíše
   * tento text (číslo nebo název záznamu; bez ohledu na velikost písmen a okolní mezery). Vrací opsaný text.
   */
  opsat?: string;
  /**
   * Zavře dialog klik na tmavé pozadí? Výchozí: ano u potvrzení bez polí, ne u dialogu s polem důvodu nebo opisu
   * (rozepsaný text by se ztratil). Vždy jen když stisk myši začal i skončil na pozadí.
   */
  zavritKlikemMimo?: boolean;
  /**
   * Široké okno (920 px, jako širší modal) pro rozsáhlý náhled s tabulkami — jinak se tabulka lomí po dvou třech slovech.
   * Na tabletu je okno přes celou šířku obrazovky, na telefonu přes celou obrazovku. Věta `text` zůstává nahoře
   * a tučně, souhrn v `obsah` se posouvá uvnitř okna; titul a tlačítka jsou vidět vždy (to platí pro každý dialog).
   */
  sirsi?: boolean;
}

export interface DialogKontext {
  potvrdit: (moznosti: MoznostiDialogu) => Promise<string | null>;
}

interface Otevreny extends MoznostiDialogu {
  vyridit: (vysledek: string | null) => void;
}

const Kontext = createContext<DialogKontext | null>(null);

export function PoskytovatelDialogu({ children }: { children: ReactNode }) {
  const [otevreny, setOtevreny] = useState<Otevreny | null>(null);

  const potvrdit = useCallback(
    (moznosti: MoznostiDialogu) =>
      new Promise<string | null>((vyridit) => {
        setOtevreny({ ...moznosti, vyridit });
      }),
    [],
  );

  const hodnota = useMemo<DialogKontext>(() => ({ potvrdit }), [potvrdit]);

  const uzavrit = (vysledek: string | null) => {
    if (!otevreny) return;
    setOtevreny(null);
    otevreny.vyridit(vysledek);
  };

  return (
    <Kontext.Provider value={hodnota}>
      {children}
      {otevreny && <Dialog moznosti={otevreny} uzavrit={uzavrit} />}
    </Kontext.Provider>
  );
}

function Dialog({
  moznosti,
  uzavrit,
}: {
  moznosti: MoznostiDialogu;
  uzavrit: (vysledek: string | null) => void;
}) {
  const [hodnota, setHodnota] = useState(moznosti.vychozi ?? '');
  const [chybiDuvod, setChybiDuvod] = useState(false);
  const prvniPrvek = useRef<HTMLInputElement | HTMLButtonElement | null>(null);
  const idTitul = 'dialog-titul';
  const idText = 'dialog-text';
  const idDuvod = 'dialog-duvod';
  const duvodPovinny = Boolean(moznosti.duvod) && moznosti.duvodPovinny !== false;
  const opsat = moznosti.opsat?.trim() || '';
  const [opsano, setOpsano] = useState('');
  const klikemMimo = moznosti.zavritKlikemMimo ?? !(moznosti.duvod || opsat);
  const podklad = usePodklad(klikemMimo ? () => uzavrit(null) : null);
  const opsanoSedi = !opsat || opsano.trim().toLowerCase() === opsat.toLowerCase();

  // Fokus dovnitř dialogu; Esc = Zrušit.
  useEffect(() => {
    prvniPrvek.current?.focus();
    const naKlavesu = (udalost: KeyboardEvent) => {
      if (udalost.key === 'Escape') {
        udalost.preventDefault();
        uzavrit(null);
      }
    };
    document.addEventListener('keydown', naKlavesu);
    return () => document.removeEventListener('keydown', naKlavesu);
    // Dialog žije jen po dobu jednoho otevření; `uzavrit` se mezitím nemění.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const naOdeslat = (udalost: FormEvent) => {
    udalost.preventDefault();
    if (!opsanoSedi) return;
    const duvod = hodnota.trim();
    if (duvodPovinny && !duvod) {
      setChybiDuvod(true);
      return;
    }
    uzavrit(duvod);
  };

  return (
    <div className="prekryv dialog-podklad" {...podklad} data-testid="dialog-podklad">
      <form
        className={`dialog${moznosti.sirsi ? ' dialog--sirsi' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitul}
        aria-describedby={moznosti.text ? idText : undefined}
        data-screen-label="Potvrzení"
        onClick={(udalost) => udalost.stopPropagation()}
        onSubmit={naOdeslat}
      >
        <h2 id={idTitul} className="dialog__titul">
          {moznosti.titul}
        </h2>
        {moznosti.text && (
          <p id={idText} className="dialog__text">
            {moznosti.text}
          </p>
        )}
        {moznosti.obsah && <div className="dialog__obsah">{moznosti.obsah}</div>}
        {opsat && (
          <div className="pole dialog__duvod">
            <label htmlFor="dialog-opsat" className="pole__popisek pole__popisek--povinne">
              Pro potvrzení opište: <strong>{opsat}</strong>
            </label>
            <input
              id="dialog-opsat"
              ref={(prvek) => {
                prvniPrvek.current = prvek;
              }}
              className="vstup"
              value={opsano}
              autoComplete="off"
              onChange={(udalost) => setOpsano(udalost.target.value)}
              data-testid="dialog-opsat"
            />
          </div>
        )}
        {moznosti.duvod && (
          <div className="pole dialog__duvod">
            <label
              htmlFor={idDuvod}
              className={`pole__popisek${duvodPovinny ? ' pole__popisek--povinne' : ''}`}
            >
              {moznosti.duvod}
            </label>
            <input
              id={idDuvod}
              ref={(prvek) => {
                if (!opsat) prvniPrvek.current = prvek;
              }}
              className={`vstup${chybiDuvod ? ' vstup--chyba' : ''}`}
              value={hodnota}
              onChange={(udalost) => {
                setHodnota(udalost.target.value);
                setChybiDuvod(false);
              }}
              aria-invalid={chybiDuvod || undefined}
              aria-describedby={chybiDuvod ? `${idDuvod}-chyba` : undefined}
            />
            {chybiDuvod && (
              <p id={`${idDuvod}-chyba`} className="pole__chyba">
                {moznosti.duvod} — vyplňte prosím.
              </p>
            )}
          </div>
        )}
        <div className="dialog__tlacitka">
          <button
            type="button"
            className="tlacitko tlacitko--sekundarni"
            onClick={() => uzavrit(null)}
          >
            {moznosti.zrusit ?? 'Zrušit'}
          </button>
          <button
            type="submit"
            ref={(prvek) => {
              if (!moznosti.duvod && !opsat) prvniPrvek.current = prvek;
            }}
            disabled={!opsanoSedi}
            className={`tlacitko ${moznosti.nebezpecne === false ? 'tlacitko--primarni' : 'tlacitko--nebezpecne'}`}
          >
            {moznosti.tlacitko ?? 'Potvrdit'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function useDialog(): DialogKontext {
  const kontext = useContext(Kontext);
  if (!kontext) {
    throw new Error('useDialog je nutné volat uvnitř PoskytovatelDialogu.');
  }
  return kontext;
}
